import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, IconButton, List, ListItem, ListItemButton, ListItemText, Typography } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import moment, { Moment } from "moment";
import { TDatePickerView } from ".";
import { DateCalendar } from "@mui/x-date-pickers";
import CloseIcon from '@mui/icons-material/Close';

export default function DialogIntervaloDate({
    open,
    fieldValues,
    onSubmit,
    handleClose,
    datePickerView,
    formatString,
    selectedOption,
    possuiBtnLimpar = false,
    onChangeOption
}: DialogIntervaloDateProps) {
    
    const dataAtual = moment();

    const [data, setData] = useState(fieldValues)

    //Valores importantes
    const btnDisabled = useMemo(() => !data.inicial && !data.final, [data])

    const periodoNaoCustomizavel = useMemo(() => selectedOption.label !== "Periodo customizado", [selectedOption])

    const { inicioSemanaAtual, fimSemanaAtual } = useMemo(() => {
        const diaDaSemana = dataAtual.day();

        const inicioSemanaAtual = moment(dataAtual).subtract(diaDaSemana, 'days');
        const fimSemanaAtual = moment(dataAtual).add(6 - diaDaSemana, 'days');

        return { inicioSemanaAtual, fimSemanaAtual }
    }, [dataAtual])

    const { inicioSemanaPassada, fimSemanaPassada } = useMemo(() => {
        const diaDaSemana = dataAtual.day();

        const inicioSemanaPassada = moment(dataAtual).subtract(7 + diaDaSemana, 'days');
        const fimSemanaPassada = moment(dataAtual).subtract(1 + diaDaSemana, 'days');

        return { inicioSemanaPassada, fimSemanaPassada }
    }, [dataAtual])

    const { inicioMesAtual, fimMesAtual } = useMemo(() => {
        const inicioMesAtual = moment(dataAtual).startOf('month');
        const fimMesAtual = moment(dataAtual).endOf('month');

        return { inicioMesAtual, fimMesAtual }
    }, [dataAtual])

    const { inicioMesPassado, fimMesPassado } = useMemo(() => {
        const inicioMesPassado = moment(dataAtual).subtract(1, 'month').startOf('month');
        const fimMesPassado = moment(dataAtual).subtract(1, 'month').endOf('month');

        return { inicioMesPassado, fimMesPassado }
    }, [dataAtual])

    const periodoOptions: TPeriodoOption[] = [
        { label: "Hoje", dataInicial: moment(), dataFinal: moment() },
        { label: "Esta semana", dataInicial: inicioSemanaAtual, dataFinal: fimSemanaAtual },
        { label: "Semana passada", dataInicial: inicioSemanaPassada, dataFinal: fimSemanaPassada },
        { label: "Este mês", dataInicial: inicioMesAtual, dataFinal: fimMesAtual },
        { label: "Mês passado", dataInicial: inicioMesPassado, dataFinal: fimMesPassado },
        { label: "Periodo customizado", dataInicial: moment(), dataFinal: moment() },
    ]
    //

    //funcoes manipuladoras
    const handleChangeDate = useCallback((prop: "inicial" | "final", date: Moment) => {
        setData(prevState => ({ ...prevState, [prop]: date }))
    }, [])

    const handleSubmit = useCallback(() => {
        onSubmit(data)
        handleClose()
    }, [onSubmit, handleClose, data])

    const handleLimpar = () => {
        const novoData = { inicial: null, final: null };
        setData(novoData)
        onSubmit(novoData)
        handleClose()
    }
    //

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle id="alert-dialog-title">
                Selecionar período
            </DialogTitle>
            {
                possuiBtnLimpar &&
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={(theme) => ({
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: theme.palette.grey[500],
                    })}
                >
                    <CloseIcon />
                </IconButton>
            }
            <DialogContent>
                <Box>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 3 }}>
                            <Box py={2}>
                                <List disablePadding>
                                    {
                                        periodoOptions.map((i, index) => (
                                            <ListItem
                                                disablePadding
                                                key={index}
                                            >
                                                <ListItemButton
                                                    onClick={() => {
                                                        onChangeOption(i)
                                                        if(i.label === "Periodo customizado") return
                                                        handleChangeDate("inicial", i.dataInicial)
                                                        handleChangeDate("final", i.dataFinal)
                                                    }}
                                                    selected={selectedOption.label === i.label}
                                                >
                                                    <ListItemText
                                                        primary={i.label}
                                                        primaryTypographyProps={{
                                                            variant: "subtitle2",
                                                            color: "primary"
                                                        }}
                                                    />
                                                </ListItemButton>
                                            </ListItem>
                                        ))
                                    }
                                </List>
                            </Box>
                        </Grid>

                        <Grid size={{ xs: 12, md: 4.5 }}>    
                            <Box display="flex" height="100%">
                                <Divider orientation="vertical" sx={{display:{xs: "none", md: "block"}}} />
                                <Box>
                                    <ValuePrinter
                                        label="Inicio do período"
                                        date={data.inicial}
                                    />
                                    <DateCalendar
                                        value={data.inicial}
                                        disabled={periodoNaoCustomizavel}
                                        onChange={(date) => handleChangeDate("inicial", date as Moment)}
                                    />
                                </Box>
                                <Divider orientation="vertical" sx={{display:{xs: "none", md: "block"}}}/>
                            </Box>
                        </Grid>

                        <Grid size={{ xs: 12, md: 4.5 }}> 
                            <Box>
                                <ValuePrinter
                                    label="Fim do período"
                                    date={data.final}
                                />
                                <DateCalendar
                                    value={data.final}
                                    disabled={periodoNaoCustomizavel}
                                    onChange={(date) => handleChangeDate("final", date as Moment)}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={possuiBtnLimpar ? handleLimpar : handleClose}
                >
                    {possuiBtnLimpar ? "Limpar" : "Fechar"}
                </Button>
                <Button
                    autoFocus
                    disabled={btnDisabled}
                    variant="contained"
                    onClick={handleSubmit}
                >
                    Confirmar
                </Button>
            </DialogActions>
        </Dialog>
    )
}

function ValuePrinter({ label, date }: ValuePrinterProps) {
    return (
        <Box mx={2}>
            <Typography variant="caption" color="gray">{label}</Typography>
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                border="1px solid #ccc"
                borderRadius={2}
                py={1}
            >
                <Typography color="primary" variant="subtitle2">{date ? date.format("DD/MM/YYYY") : "-"}</Typography>
            </Box>
        </Box>
    )
}

interface DialogIntervaloDateProps {
    open: boolean,
    handleClose: () => void,
    fieldValues: TDateState,
    onSubmit: (arg: TDateState) => void,
    datePickerView: TDatePickerView,
    formatString: string,
    selectedOption: TPeriodoOption,
    onChangeOption: (arg: TPeriodoOption) => void,
    possuiBtnLimpar?: boolean
}

type TDateState = {
    inicial: Moment | null,
    final: Moment | null
}

interface ValuePrinterProps {
    label: string,
    date: Moment | null
}

export type TPeriodoOption = {
    label: string,
    dataInicial: Moment,
    dataFinal: Moment
}